/**
 * Drucken aus dem Browser.
 *
 * Vor dem Druck („beforeprint“) gilt immer das helle Farbschema ohne
 * Hintergrundmuster, zugeklappte Bereiche (zum Beispiel die Nährwerte) gehen
 * auf und Animationen zeigen ihr Standbild. Nach dem Druck („afterprint“)
 * kehrt alles zurück. Was nicht aufs Papier gehört, trägt die Klasse
 * „print:hidden“.
 */
import { useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';

let printing = false;
const listeners = new Set<() => void>();

/** Benachrichtigt die Oberfläche. Synchron, damit der Druck den neuen Stand sieht. */
function setPrinting(value: boolean): void {
  if (printing === value) {
    return;
  }
  printing = value;
  flushSync(() => {
    for (const listener of listeners) {
      listener();
    }
  });
}

/** Wird gerade gedruckt? */
export function usePrinting(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => printing,
    () => false,
  );
}

/** Zustand vor dem Druck, um ihn danach wiederherzustellen. */
interface Saved {
  theme: string | undefined;
  background: string | undefined;
  closed: HTMLDetailsElement[];
}

/** Richtet die Druckvorbereitung ein (einmal beim Start). */
export function startPrintSupport(): void {
  let saved: Saved | null = null;
  const before = () => {
    if (saved !== null) {
      return;
    }
    const root = document.documentElement;
    const closed = [...document.querySelectorAll<HTMLDetailsElement>('details:not([open])')];
    saved = { theme: root.dataset.theme, background: root.dataset.background, closed };
    root.dataset.theme = 'light';
    delete root.dataset.background;
    closed.forEach((element) => {
      element.open = true;
    });
    setPrinting(true);
  };
  const after = () => {
    if (saved === null) {
      return;
    }
    const root = document.documentElement;
    if (saved.theme === undefined) {
      delete root.dataset.theme;
    } else {
      root.dataset.theme = saved.theme;
    }
    if (saved.background !== undefined) {
      root.dataset.background = saved.background;
    }
    saved.closed.forEach((element) => {
      element.open = false;
    });
    saved = null;
    setPrinting(false);
  };
  window.addEventListener('beforeprint', before);
  window.addEventListener('afterprint', after);
  // Manche Browser melden den Druck nur über die Medienabfrage.
  window.matchMedia('print').addEventListener('change', (event) => (event.matches ? before() : after()));
}

/** Öffnet den Druckdialog des Browsers. */
export function printPage(): void {
  window.print();
}
